/**
 * The disbursal trend chart above the Disbursed Loan Details register
 * (plans/disbursed_loans_dashboard_plan.md §5.3).
 *
 * WHAT IS DRAWN, AND WHY EACH LINE IS THE SHAPE IT IS
 * --------------------------------------------------
 * x-axis is the DAY OF MONTH (1..31), which is what makes "this month so far"
 * and "all of last month" comparable on one axis at all — calendar dates would
 * put the two months side by side instead of on top of each other.
 *
 * 1. **Solid line — current month cumulative.** Plotted straight from
 *    `series.current`, which the backend stops at today. It is deliberately NOT
 *    zero-filled to month end: a flat line running into the future reads as "we
 *    disbursed nothing on the 20th" rather than "the 20th has not happened yet".
 *    This component must therefore never extrapolate it either.
 *
 * 2. **Comparison line — last month cumulative, FULL month.** Spans the whole
 *    width so the current month can be read against it at any day.
 *
 * 3. **Dotted line — the run-rate forecast.** A TWO-POINT series: today's real
 *    cumulative value, and `projected_month_end_*` on the last day of the month,
 *    joined with `connectNulls`. Anchoring it on today's actual point is the
 *    whole reason it reads as a continuation of the solid line rather than as a
 *    third, floating series. It is hidden on the last day of the month, where
 *    there is nothing left to forecast.
 *
 * 4. **`ReferenceLine` at last month's full total**, labelled "Last month
 *    total" — the target the month is being run against.
 *
 * CONTROLS
 * --------
 * * Amount / Count toggle — switches the plotted field (`cum_amount` vs
 *   `cum_count`), the y-axis formatter (₹ lakh/crore vs plain integer) AND the
 *   forecast field. All three must move together or the forecast would be drawn
 *   in the wrong unit.
 * * 7 / 14 / 21-day window — reads the PRE-FETCHED `run_rate[String(n)]`. All
 *   three windows arrive in the one metrics response, so this never refetches.
 *
 * Money arrives as decimal strings; `Number()` is applied only here, at the
 * point of plotting, because recharts cannot plot a string.
 */
import React from "react";
import {
    ComposedChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
    ResponsiveContainer,
} from "recharts";
import { LineChart as LineChartIcon } from "lucide-react";

import { DisbursedLoanMetrics, RunRateEntry } from "./lmsStaffApi";
import { formatAmount, formatCompactAmount } from "./formatters";

/** The run-rate lookback windows the backend returns (RUN_RATE_WINDOWS). */
const RUN_RATE_WINDOWS = [7, 14, 21] as const;
type RunRateWindow = (typeof RUN_RATE_WINDOWS)[number];

type ChartMetric = "amount" | "count";

interface DisbursedLoansTrendChartProps {
    metrics?: DisbursedLoanMetrics;
    isLoading?: boolean;
    isError?: boolean;
    /**
     * When set (format `"YYYY-MM"`), the chart title and legend labels
     * reflect the selected month instead of "This Month / Last Month".
     * Empty string / undefined means live MTD (existing behaviour).
     */
    referenceMonth?: string;
}

/** One merged x-axis row. `null` means "no point here", not "zero". */
interface ChartRow {
    day: number;
    current: number | null;
    lastMonth: number | null;
    /** Non-null on exactly two days: today and month end. */
    forecast: number | null;
    /** That day's own (non-cumulative) figure, for the tooltip. */
    currentDelta: number | null;
    lastMonthDelta: number | null;
}

const COLOURS = {
    current: "#059669", // emerald-600 — this month, the primary series
    lastMonth: "#94a3b8", // slate-400 — the comparison, deliberately muted
    forecast: "#7c3aed", // violet-600 — projection, visually distinct
    reference: "#f59e0b", // amber-500 — the target line
};

const DisbursedLoansTrendChart: React.FC<DisbursedLoansTrendChartProps> = ({
    metrics,
    isLoading,
    isError,
    referenceMonth,
}) => {
    const [metric, setMetric] = React.useState<ChartMetric>("amount");
    const [windowDays, setWindowDays] = React.useState<RunRateWindow>(7);

    const isAmount = metric === "amount";

    /**
     * Human-readable label for the selected month, e.g. "Sep 2026".
     * Used in the chart title and legend when a past month is selected.
     * Falls back to "This Month" / "Last Month" for live MTD.
     */
    const selectedMonthLabel = referenceMonth
        ? new Date(referenceMonth + "-01").toLocaleString("en-IN", {
              month: "short",
              year: "numeric",
          })
        : null;

    /** The month before `referenceMonth`, e.g. "Aug 2026" for "2026-09". */
    const prevMonthLabel = React.useMemo(() => {
        if (!referenceMonth) return null;
        const [year, month] = referenceMonth.split("-").map(Number);
        const prev = new Date(year, month - 2, 1); // month-2 because month is 1-based
        return prev.toLocaleString("en-IN", { month: "short", year: "numeric" });
    }, [referenceMonth]);

    const currentSeriesLabel = selectedMonthLabel ?? "This month (actual)";
    const lastMonthSeriesLabel = prevMonthLabel
        ? `${prevMonthLabel} (full month)`
        : "Last month";
    const chartTitle = selectedMonthLabel
        ? `Cumulative disbursals — ${selectedMonthLabel} vs ${prevMonthLabel ?? "prev month"}`
        : "Cumulative disbursals — this month vs last month";
    const referenceLineLabel = prevMonthLabel
        ? `${prevMonthLabel} total`
        : "Last month total";

    // `run_rate` keys are STRINGS ("7"/"14"/"21") because JSON object keys always
    // are — indexing with the number would silently yield undefined.
    const runRate: RunRateEntry | undefined =
        metrics?.run_rate?.[String(windowDays)];

    const rows = React.useMemo<ChartRow[]>(() => {
        if (!metrics) return [];

        const current = metrics.series.current;
        const lastMonth = metrics.series.last_month;

        const pick = (point: { cum_amount: string; cum_count: number }) =>
            isAmount ? Number(point.cum_amount) : point.cum_count;
        const pickDelta = (point: { amount: string; count: number }) =>
            isAmount ? Number(point.amount) : point.count;

        const currentByDay = new Map(current.map((p) => [p.day, p]));
        const lastByDay = new Map(lastMonth.map((p) => [p.day, p]));

        // The axis must be wide enough for BOTH months (e.g. a 30-day current
        // month compared against a 31-day last month) and for the forecast's
        // month-end anchor.
        const span = Math.max(
            metrics.series.days_in_current_month,
            metrics.series.days_in_last_month
        );

        const todayDay = current.length ? current[current.length - 1].day : 0;
        const todayCumulative = current.length
            ? pick(current[current.length - 1])
            : 0;
        const monthEndDay = metrics.series.days_in_current_month;

        // The forecast is only meaningful while days remain. On the last day of
        // the month `days_remaining` is 0 and a "projection" would just restate
        // today's actual, so the line is suppressed entirely.
        const showForecast =
            !!runRate && runRate.days_remaining > 0 && current.length > 0;
        const projected = runRate
            ? isAmount
                ? Number(runRate.projected_month_end_amount)
                : runRate.projected_month_end_count
            : null;

        const out: ChartRow[] = [];
        for (let day = 1; day <= span; day += 1) {
            const cur = currentByDay.get(day);
            const last = lastByDay.get(day);

            let forecast: number | null = null;
            if (showForecast && projected !== null) {
                // Exactly two non-null points, joined by `connectNulls` — the
                // first IS today's real cumulative value, so the dotted line
                // leaves the solid line rather than hanging in space.
                if (day === todayDay) forecast = todayCumulative;
                else if (day === monthEndDay) forecast = projected;
            }

            out.push({
                day,
                // undefined -> null: a missing day must be a GAP, not a zero.
                // `series.current` legitimately has no points after today.
                current: cur ? pick(cur) : null,
                lastMonth: last ? pick(last) : null,
                forecast,
                currentDelta: cur ? pickDelta(cur) : null,
                lastMonthDelta: last ? pickDelta(last) : null,
            });
        }
        return out;
    }, [metrics, isAmount, runRate]);

    const formatValue = React.useCallback(
        (value: number) =>
            isAmount
                ? formatCompactAmount(value)
                : Math.round(value).toLocaleString("en-IN"),
        [isAmount]
    );

    const formatValueFull = React.useCallback(
        (value: number) =>
            isAmount
                ? formatAmount(value)
                : `${Math.round(value).toLocaleString("en-IN")} loans`,
        [isAmount]
    );

    if (isLoading && !metrics) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
                <div className="mt-4 h-72 animate-pulse rounded bg-gray-100" />
            </div>
        );
    }

    if (isError || !metrics) {
        return (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-6 text-center text-xs text-gray-500">
                Disbursal trend is unavailable right now. The register below is
                unaffected.
            </div>
        );
    }

    // EXPLICIT empty state (plan §5.4): an axis-less blank chart box looks
    // broken, whereas "no disbursals this month yet" is a fact about the data.
    // "All zero" counts as empty — the backend zero-fills, so a month with no
    // activity still returns points.
    const hasCurrentData =
        metrics.series.current.length > 0 &&
        metrics.series.current.some(
            (p) => Number(p.cum_amount) > 0 || p.cum_count > 0
        );

    const lastMonthTotal = isAmount
        ? Number(metrics.last_month_full.amount)
        : metrics.last_month_full.count;

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
            <div className="mb-4 flex flex-col items-start justify-between gap-3 lg:flex-row lg:items-center">
                <div>
                    <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
                        <LineChartIcon className="h-4 w-4 text-intellend-green" />
                        {chartTitle}
                    </h2>
                    <p className="mt-1 text-xs text-gray-500">
                        IST day buckets, all non-simulated loans. Solid line is
                        actual data to date; the dotted line projects month end from
                        the last {windowDays} days.
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {/* Amount / Count toggle */}
                    <div className="inline-flex overflow-hidden rounded-lg border border-gray-200">
                        {(["amount", "count"] as ChartMetric[]).map((option) => (
                            <button
                                key={option}
                                type="button"
                                onClick={() => setMetric(option)}
                                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                                    metric === option
                                        ? "bg-intellend-green text-white"
                                        : "bg-white text-gray-600 hover:bg-gray-50"
                                }`}
                            >
                                {option === "amount" ? "Amount" : "Count"}
                            </button>
                        ))}
                    </div>

                    {/* Run-rate window. Purely client-side: all three windows are
                        already in the one metrics response. */}
                    <select
                        value={windowDays}
                        onChange={(e) =>
                            setWindowDays(Number(e.target.value) as RunRateWindow)
                        }
                        className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-intellend-green/30"
                        aria-label="Run-rate window"
                    >
                        {RUN_RATE_WINDOWS.map((n) => (
                            <option key={n} value={n}>
                                {n}-day run rate
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {!hasCurrentData ? (
                <div className="flex h-64 flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 text-center">
                    <p className="text-sm font-medium text-gray-600">
                        No disbursals this month yet
                    </p>
                    <p className="mt-1 max-w-md text-xs text-gray-500">
                        Last month closed at{" "}
                        {formatCompactAmount(metrics.last_month_full.amount)} across{" "}
                        {metrics.last_month_full.count.toLocaleString("en-IN")}{" "}
                        {metrics.last_month_full.count === 1 ? "loan" : "loans"}. The
                        chart appears as soon as the first disbursal of the month is
                        recorded.
                    </p>
                </div>
            ) : (
                <>
                    <div className="h-72 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart
                                data={rows}
                                margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="#f1f5f9"
                                />
                                <XAxis
                                    dataKey="day"
                                    tick={{ fontSize: 11, fill: "#6b7280" }}
                                    tickLine={false}
                                    label={{
                                        value: "Day of month",
                                        position: "insideBottom",
                                        offset: -2,
                                        fontSize: 10,
                                        fill: "#9ca3af",
                                    }}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: "#6b7280" }}
                                    tickLine={false}
                                    axisLine={false}
                                    width={isAmount ? 78 : 56}
                                    tickFormatter={(value: number) =>
                                        formatValue(value)
                                    }
                                />
                                <Tooltip
                                    content={
                                        <TrendTooltip
                                            formatValue={formatValueFull}
                                            isAmount={isAmount}
                                        />
                                    }
                                />
                                <Legend
                                    wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                                />
                                {/* Previous month's FULL total — the target the
                                    selected/current month is being run against. */}
                                <ReferenceLine
                                    y={lastMonthTotal}
                                    stroke={COLOURS.reference}
                                    strokeDasharray="6 4"
                                    label={{
                                        value: referenceLineLabel,
                                        position: "insideTopRight",
                                        fontSize: 10,
                                        fill: "#b45309",
                                    }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="lastMonth"
                                    name={lastMonthSeriesLabel}
                                    stroke={COLOURS.lastMonth}
                                    strokeWidth={2}
                                    dot={false}
                                    // Last month is complete, so a null can only be
                                    // a day the shorter month does not have. Do NOT
                                    // bridge it — that would invent a value.
                                    connectNulls={false}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="current"
                                    name={currentSeriesLabel}
                                    stroke={COLOURS.current}
                                    strokeWidth={2.5}
                                    dot={false}
                                    activeDot={{ r: 4 }}
                                    connectNulls={false}
                                />
                                {/* The forecast: two points, bridged. */}
                                <Line
                                    type="linear"
                                    dataKey="forecast"
                                    name={`Forecast (${windowDays}-day run rate)`}
                                    stroke={COLOURS.forecast}
                                    strokeWidth={2}
                                    strokeDasharray="5 5"
                                    dot={{ r: 3, fill: COLOURS.forecast }}
                                    connectNulls
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>

                    {/* The forecast's own arithmetic, stated in words, so the
                        dotted line is auditable rather than magic. */}
                    {runRate && (
                        <p className="mt-3 text-[11px] leading-relaxed text-gray-500">
                            {runRate.days_remaining > 0 ? (
                                <>
                                    Last {runRate.window_days} days (
                                    {runRate.window_start} to {runRate.window_end})
                                    averaged{" "}
                                    <span className="font-medium text-gray-700">
                                        {isAmount
                                            ? formatCompactAmount(
                                                  runRate.daily_avg_amount
                                              )
                                            : runRate.daily_avg_count.toFixed(2)}
                                    </span>{" "}
                                    per day, so{" "}
                                    {runRate.days_remaining === 1
                                        ? "1 remaining day"
                                        : `${runRate.days_remaining} remaining days`}{" "}
                                    project a month end of{" "}
                                    <span className="font-medium text-gray-700">
                                        {isAmount
                                            ? formatCompactAmount(
                                                  runRate.projected_month_end_amount
                                              )
                                            : `${runRate.projected_month_end_count.toLocaleString(
                                                  "en-IN"
                                              )} loans`}
                                    </span>
                                    .
                                </>
                            ) : (
                                <>
                                    The month is complete, so no forecast is drawn —
                                    the solid line is the final figure.
                                </>
                            )}
                        </p>
                    )}
                </>
            )}
        </div>
    );
};

/**
 * Tooltip showing BOTH months' cumulative value for the hovered day plus that
 * day's own delta — the day-over-day movement is the thing a cumulative line
 * hides, so reading it off the tooltip is the point.
 *
 * Typed with an explicit local prop shape rather than recharts' own
 * `TooltipProps` generics (which vary across versions and would break on a
 * minor bump) -- `active`/`payload`/`label` mirror what recharts actually
 * passes to a custom tooltip content component, and `formatValue`/`isAmount`
 * are this chart's own extra props threaded through via `content={...}`.
 */
interface TrendTooltipProps {
    active?: boolean;
    payload?: Array<{ payload: ChartRow }>;
    label?: string | number;
    formatValue: (value: number) => string;
    isAmount: boolean;
}

const TrendTooltip: React.FC<TrendTooltipProps> = ({
    active,
    payload,
    label,
    formatValue,
    isAmount,
}) => {
    if (!active || !payload || !payload.length) return null;

    const row: ChartRow | undefined = payload[0]?.payload;
    if (!row) return null;

    const deltaLabel = (value: number | null) => {
        if (value === null) return null;
        return isAmount
            ? formatCompactAmount(value)
            : `${value.toLocaleString("en-IN")}`;
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-3 text-xs shadow-md">
            <p className="mb-1.5 font-semibold text-gray-900">Day {label}</p>
            <div className="space-y-1">
                {row.current !== null ? (
                    <div>
                        <span
                            className="mr-1 inline-block h-2 w-2 rounded-full"
                            style={{ backgroundColor: COLOURS.current }}
                        />
                        <span className="text-gray-600">This month: </span>
                        <span className="font-medium text-gray-900">
                            {formatValue(row.current)}
                        </span>
                        {row.currentDelta !== null && (
                            <span className="text-gray-400">
                                {" "}
                                (+{deltaLabel(row.currentDelta)} that day)
                            </span>
                        )}
                    </div>
                ) : (
                    <div className="text-gray-400">
                        This month: not yet reached
                    </div>
                )}
                {row.lastMonth !== null && (
                    <div>
                        <span
                            className="mr-1 inline-block h-2 w-2 rounded-full"
                            style={{ backgroundColor: COLOURS.lastMonth }}
                        />
                        <span className="text-gray-600">Last month: </span>
                        <span className="font-medium text-gray-900">
                            {formatValue(row.lastMonth)}
                        </span>
                        {row.lastMonthDelta !== null && (
                            <span className="text-gray-400">
                                {" "}
                                (+{deltaLabel(row.lastMonthDelta)} that day)
                            </span>
                        )}
                    </div>
                )}
                {row.forecast !== null && (
                    <div>
                        <span
                            className="mr-1 inline-block h-2 w-2 rounded-full"
                            style={{ backgroundColor: COLOURS.forecast }}
                        />
                        <span className="text-gray-600">Forecast: </span>
                        <span className="font-medium text-gray-900">
                            {formatValue(row.forecast)}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DisbursedLoansTrendChart;
